import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, naira, formatApiError } from "@/lib/api";
import { NIGERIAN_STATES } from "@/lib/constants";
import { lgasForState } from "@/lib/lga";
import { Loader2, CreditCard, Landmark, Copy, Wallet } from "lucide-react";

export function PaymentDialog({ sponsorEmail }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);
  const [f, setF] = useState({ beneficiaries: 0, amount_per: 0, state: "", lga: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const subtotal = (Number(f.beneficiaries) || 0) * (Number(f.amount_per) || 0);
  const fee = Math.round(subtotal * 0.1);
  const total = subtotal + fee;

  const onOpen = (o) => {
    setOpen(o);
    if (o) { setPay(null); setF({ beneficiaries: 0, amount_per: 0, state: "", lga: "" }); api.get("/sponsor/payment-config").then((r) => setCfg(r.data)).catch(() => setCfg({ paystack_configured: false })); }
  };

  const start = async (method) => {
    if (total <= 0) return toast.error("Enter beneficiaries and amount per beneficiary.");
    setBusy(true);
    try {
      const { data } = await api.post("/sponsor/payments/initialize", {
        ...f, email: sponsorEmail || "sponsor@tmf.local", method,
        callback_url: `${window.location.origin}/payment/callback`,
      });
      if (data.authorization_url) { window.location.href = data.authorization_url; return; }
      setPay(data);
      if (method === "paystack" && !data.paystack_configured) toast.info("Online payment not configured yet — use bank transfer.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markTransferred = async () => {
    try { await api.post(`/sponsor/payments/${pay.ref}/mark-transfer`); toast.success("Thank you — we'll confirm your transfer shortly."); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button data-testid="fund-pay-btn" className="rounded-full h-11 px-4 bg-blue-600 hover:bg-blue-700"><Wallet className="w-4 h-4 mr-2" /> Fund / Pay</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Fund a Programme</DialogTitle></DialogHeader>
        {!pay ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Beneficiaries</Label><Input data-testid="pay-beneficiaries" type="number" min="0" value={f.beneficiaries || ""} placeholder="0" onFocus={(e) => e.target.select()} onChange={(e) => set("beneficiaries", +e.target.value)} className="mt-1.5 h-11" /></div>
              <div><Label>Amount / Beneficiary (₦)</Label><Input data-testid="pay-amount" type="number" min="0" value={f.amount_per || ""} placeholder="0" onFocus={(e) => e.target.select()} onChange={(e) => set("amount_per", +e.target.value)} className="mt-1.5 h-11" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State of Interest</Label>
                <Select value={f.state} onValueChange={(v) => setF((p) => ({ ...p, state: v, lga: "" }))}>
                  <SelectTrigger data-testid="pay-state" className="mt-1.5 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>LGA</Label>
                <Select value={f.lga} onValueChange={(v) => set("lga", v)} disabled={!f.state}>
                  <SelectTrigger data-testid="pay-lga" className="mt-1.5 h-11"><SelectValue placeholder={f.state ? "Select" : "State first"} /></SelectTrigger>
                  <SelectContent>{lgasForState(f.state).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 space-y-2" data-testid="pay-breakdown">
              <div className="flex items-center justify-between text-sm"><span className="text-gray-600">Programme value (beneficiaries × amount)</span><span data-testid="pay-subtotal" className="font-semibold text-emerald-950">{naira(subtotal)}</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-gray-600">Programme Delivery & Impact Management Fee (10%)</span><span data-testid="pay-fee" className="font-semibold text-emerald-950">{naira(fee)}</span></div>
              <div className="flex items-center justify-between border-t border-emerald-200 pt-2">
                <span className="text-sm font-semibold text-gray-700">Total to fund</span>
                <span data-testid="pay-total" className="font-display font-bold text-2xl text-emerald-800">{naira(total)}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="pay-now-btn" onClick={() => start("paystack")} disabled={busy} className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 rounded-full">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Pay Now (Paystack)</>}
              </Button>
              <Button data-testid="pay-transfer-btn" onClick={() => start("moniepoint")} disabled={busy} variant="outline" className="flex-1 h-12 rounded-full border-emerald-200 text-emerald-800">
                <Landmark className="w-4 h-4 mr-2" /> Bank Transfer
              </Button>
            </div>
            {cfg && !cfg.paystack_configured && <p className="text-xs text-amber-600 text-center">Online card payment is being set up — bank transfer is available now.</p>}
          </div>
        ) : (
          <div className="space-y-4" data-testid="pay-transfer-details">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <div className="text-sm text-gray-600">Transfer exactly</div>
              <div className="font-display font-bold text-3xl text-emerald-800" data-testid="pay-transfer-total">{naira(pay.total)}</div>
              {pay.delivery_fee > 0 && <div className="mt-1 text-xs text-gray-500">{naira(pay.programme_value)} programme value + {naira(pay.delivery_fee)} Programme Delivery & Impact Management Fee (10%)</div>}
            </div>
            <div className="rounded-2xl border border-emerald-100 p-4 space-y-2 text-sm">
              {[["Bank", pay.moniepoint?.bank], ["Account Name", pay.moniepoint?.account_name], ["Account Number", pay.moniepoint?.account_number], ["Payment Reference", pay.ref]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">{k}</span>
                  <span className="font-semibold text-emerald-950 flex items-center gap-2">{v}
                    <button onClick={() => { navigator.clipboard.writeText(String(v || "")); toast.success("Copied"); }} className="p-1 rounded hover:bg-emerald-50"><Copy className="w-3.5 h-3.5 text-emerald-600" /></button>
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">Use the Payment Reference in your transfer narration so we can match your payment. Your funding is confirmed once received.</p>
            <Button data-testid="pay-mark-transferred-btn" onClick={markTransferred} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">I've made the transfer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
